// ─────────────────────────────────────────────────────────────────────────────
// CLOTH PHYSICS ENGINE — O3DE replacement for web
// Uses @dimforge/rapier3d-compat (Rust → WASM) for real physics
// Simulates: fabric drape, gravity, wind, body collision
// ─────────────────────────────────────────────────────────────────────────────
import * as THREE from "three";
import type { NormalizedLandmark } from "./pose";
import { POSE_IDX } from "./pose";

// ── Rapier lazy-loaded (WASM) ──────────────────────────────────────────────
type RapierModule = typeof import("@dimforge/rapier3d-compat");
let RAPIER: RapierModule | null = null;
let rapierLoading = false;

export async function loadRapier(): Promise<RapierModule | null> {
  if (RAPIER) return RAPIER;
  if (rapierLoading) {
    await new Promise<void>(res => {
      const iv = setInterval(() => { if (RAPIER || !rapierLoading) { clearInterval(iv); res(); } }, 50);
    });
    return RAPIER;
  }
  rapierLoading = true;
  try {
    const rapier = await import("@dimforge/rapier3d-compat");
    await rapier.init();
    RAPIER = rapier;
    console.log("[Cloth] Rapier3D WASM ready ✓");
  } catch (err) {
    console.warn("[Cloth] Rapier3D failed to load — cloth physics disabled", err);
  }
  rapierLoading = false;
  return RAPIER;
}

// ── Cloth simulation parameters ───────────────────────────────────────────────
export interface ClothParams {
  gravity: number;        // default -9.8
  damping: number;        // default 0.02 (air resistance)
  stiffness: number;      // default 0.8 (fabric rigidity 0=silk, 1=denim)
  windX: number;          // default 0
  windZ: number;          // default 0
  iterations: number;     // constraint solve iterations default 8
  mass: number;           // per-particle mass default 0.1kg
}

const DEFAULT_PARAMS: ClothParams = {
  gravity: -9.8,
  damping: 0.02,
  stiffness: 0.8,
  windX: 0,
  windZ: 0,
  iterations: 8,
  mass: 0.1,
};

// ── Position-Based Dynamics cloth grid ───────────────────────────────────────
// Fallback when Rapier unavailable — runs fully on JS main thread
// PBD is used in AAA games (Nvidia PhysX) — simpler than rigid body
interface Particle {
  pos: THREE.Vector3;
  prevPos: THREE.Vector3;
  velocity: THREE.Vector3;
  pinned: boolean;  // true = pinned to skeleton (shoulder/collar)
  mass: number;
}

interface DistanceConstraint {
  a: number;
  b: number;
  restLength: number;
}

export class PBDClothSim {
  private particles: Particle[] = [];
  private constraints: DistanceConstraint[] = [];
  private params: ClothParams;
  private cols: number;

  // Output geometry for Three.js
  readonly geometry: THREE.BufferGeometry;
  private posAttr: THREE.BufferAttribute;

  constructor(
    cols = 16,
    rows = 24,
    width = 0.6,   // cloth width in meters
    height = 0.8,  // cloth height in meters
    params: Partial<ClothParams> = {}
  ) {
    this.cols = cols;
    void rows; // rows stored implicitly via particle array length
    this.params = { ...DEFAULT_PARAMS, ...params };

    const dx = width / (cols - 1);
    const dy = height / (rows - 1);

    // ── Create particles ──────────────────────────────────────────────────
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = c * dx - width / 2;
        const y = -(r * dy);       // hang downward
        const z = 0;
        const pinned = r === 0;    // top row pinned to skeleton shoulders
        this.particles.push({
          pos: new THREE.Vector3(x, y, z),
          prevPos: new THREE.Vector3(x, y, z),
          velocity: new THREE.Vector3(),
          pinned,
          mass: pinned ? Infinity : this.params.mass,
        });
      }
    }

    // ── Create distance constraints ───────────────────────────────────────
    const idx = (r: number, c: number) => r * cols + c;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // Structural: horizontal
        if (c < cols - 1) this.addConstraint(idx(r, c), idx(r, c + 1));
        // Structural: vertical
        if (r < rows - 1) this.addConstraint(idx(r, c), idx(r + 1, c));
        // Shear
        if (r < rows - 1 && c < cols - 1) {
          this.addConstraint(idx(r, c), idx(r + 1, c + 1));
          this.addConstraint(idx(r, c + 1), idx(r + 1, c));
        }
        // Bending (skip-1 constraints for fabric stiffness)
        if (c < cols - 2) this.addConstraint(idx(r, c), idx(r, c + 2));
        if (r < rows - 2) this.addConstraint(idx(r, c), idx(r + 2, c));
      }
    }

    // ── Three.js buffer geometry ──────────────────────────────────────────
    const positions = new Float32Array(cols * rows * 3);
    const uvs       = new Float32Array(cols * rows * 2);
    const indices: number[] = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        uvs[i * 2]     = c / (cols - 1);
        uvs[i * 2 + 1] = 1 - r / (rows - 1);
        if (r < rows - 1 && c < cols - 1) {
          const a = i, b = i + 1, c1 = i + cols, d = i + cols + 1;
          indices.push(a, b, c1, b, d, c1);
        }
      }
    }

    this.geometry = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(positions, 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("position", this.posAttr);
    this.geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
    this.geometry.setIndex(indices);
    this.geometry.computeVertexNormals();
    this.updateGeometry();
  }

  private addConstraint(a: number, b: number) {
    const pa = this.particles[a].pos;
    const pb = this.particles[b].pos;
    this.constraints.push({ a, b, restLength: pa.distanceTo(pb) });
  }

  // ── Simulate one physics step ─────────────────────────────────────────────
  step(dt: number) {
    const { gravity, damping, windX, windZ, stiffness, iterations } = this.params;

    // Verlet integration
    for (const p of this.particles) {
      if (p.pinned) continue;
      const { pos, prevPos, velocity } = p;

      // Gravity + wind forces
      velocity.x += (windX) * dt;
      velocity.y += gravity * dt;
      velocity.z += (windZ) * dt;

      // Damping
      velocity.multiplyScalar(1 - damping);

      prevPos.copy(pos);
      pos.add(velocity.clone().multiplyScalar(dt));
    }

    // Constraint projection
    for (let iter = 0; iter < iterations; iter++) {
      for (const { a, b, restLength } of this.constraints) {
        const pa = this.particles[a];
        const pb = this.particles[b];
        const diff = pa.pos.clone().sub(pb.pos);
        const dist = diff.length();
        if (dist === 0) continue;

        const correction = diff.multiplyScalar((1 - restLength / dist) * stiffness);
        const wA = pa.pinned ? 0 : 1;
        const wB = pb.pinned ? 0 : 1;
        const total = wA + wB;
        if (total === 0) continue;

        pa.pos.sub(correction.clone().multiplyScalar(wA / total));
        pb.pos.add(correction.clone().multiplyScalar(wB / total));
      }
    }

    // Update velocity from position change
    for (const p of this.particles) {
      if (!p.pinned) {
        p.velocity.subVectors(p.pos, p.prevPos).divideScalar(dt);
      }
    }

    this.updateGeometry();
  }

  // ── Pin top-row particles to skeleton landmarks ───────────────────────────
  pinToSkeleton(
    landmarks: NormalizedLandmark[],
    isMirrored: boolean,
  ) {
    const ls = landmarks[POSE_IDX.LEFT_SHOULDER];
    const rs = landmarks[POSE_IDX.RIGHT_SHOULDER];
    if (!ls || !rs) return;

    // Spec coordinate conversion: toWorld(x,y) = (x-0.5)*4, -(y-0.5)*4
    const toW = (lm: NormalizedLandmark) => ({
      x: ((isMirrored ? 1 - lm.x : lm.x) - 0.5) * 4,
      y: -(lm.y - 0.5) * 4,
      z: lm.z * -2,
    });

    const leftClip  = toW(ls);
    const rightClip = toW(rs);

    // Distribute top row evenly between left and right shoulder
    for (let c = 0; c < this.cols; c++) {
      const t = c / (this.cols - 1);
      const p = this.particles[c]; // row 0
      p.pos.set(
        THREE.MathUtils.lerp(leftClip.x, rightClip.x, t),
        THREE.MathUtils.lerp(leftClip.y, rightClip.y, t),
        THREE.MathUtils.lerp(leftClip.z, rightClip.z, t),
      );
      p.prevPos.copy(p.pos);
    }
  }

  // ── Apply wind pulse ──────────────────────────────────────────────────────
  applyWind(x: number, z: number) {
    this.params.windX = x;
    this.params.windZ = z;
  }

  // ── Body collision — simple sphere per landmark ───────────────────────────
  applyBodyCollision(
    landmarks: NormalizedLandmark[],
    isMirrored: boolean,
    sphereRadius = 0.12,
  ) {
    const collisionJoints = [
      POSE_IDX.LEFT_SHOULDER, POSE_IDX.RIGHT_SHOULDER,
      POSE_IDX.LEFT_HIP,      POSE_IDX.RIGHT_HIP,
      POSE_IDX.LEFT_ELBOW,    POSE_IDX.RIGHT_ELBOW,
    ];

    for (const ji of collisionJoints) {
      const lm = landmarks[ji];
      if (!lm || (lm.visibility ?? 1) < 0.3) continue;
      const nx = isMirrored ? 1 - lm.x : lm.x;
      const cx = (nx - 0.5) * 4;
      const cy = -(lm.y - 0.5) * 4;
      const cz = lm.z * -2;

      for (const p of this.particles) {
        if (p.pinned) continue;
        const dx = p.pos.x - cx;
        const dy = p.pos.y - cy;
        const dz = p.pos.z - cz;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < sphereRadius && dist > 0) {
          const push = sphereRadius / dist;
          p.pos.x = cx + dx * push;
          p.pos.y = cy + dy * push;
          p.pos.z = cz + dz * push;
        }
      }
    }
  }

  private updateGeometry() {
    const pos = this.posAttr.array as Float32Array;
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      pos[i * 3]     = p.pos.x;
      pos[i * 3 + 1] = p.pos.y;
      pos[i * 3 + 2] = p.pos.z;
    }
    this.posAttr.needsUpdate = true;
    this.geometry.computeVertexNormals();
  }

  setParams(params: Partial<ClothParams>) {
    Object.assign(this.params, params);
  }

  dispose() {
    this.geometry.dispose();
  }
}

// ── Fabric preset configs ─────────────────────────────────────────────────────
export const FABRIC_PRESETS: Record<string, Partial<ClothParams>> = {
  silk:    { stiffness: 0.3, damping: 0.01, mass: 0.05, gravity: -6  },
  cotton:  { stiffness: 0.7, damping: 0.03, mass: 0.12, gravity: -9  },
  denim:   { stiffness: 0.95, damping: 0.05, mass: 0.25, gravity: -12 },
  leather: { stiffness: 0.98, damping: 0.08, mass: 0.4,  gravity: -14 },
  knit:    { stiffness: 0.5, damping: 0.02, mass: 0.08, gravity: -7  },
};
